import { SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Guid } from 'guid-typescript';
import { differenceWith, findIndex, intersectionWith, isNil, sortBy, trim } from 'lodash';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

export interface Item {
  id: string;
  name: string;
  data: any;
  selectable: boolean;
  filterMatch: boolean;
  disabled: boolean;
  sortOrder: number;
}

export interface Node {
  children?: Node[];
  item: Item;
}

export interface FlatNode {
  item: Item;
  level: number;
  expandable: boolean;
}

@Component({
  selector: 'app-list-tree',
  templateUrl: './list-tree.component.html',
  styleUrls: ['./list-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListTreeComponent implements OnInit, OnChanges {
  @Input() private data: Node[] = [];
  @Input() multiple = true;
  @Input() searchable = true;
  @Input() searchPlaceholder = 'Suchen';
  @Input() itemSize = 50;
  @Input() minBufferPx = 100;
  @Input() maxBufferPx = 200;
  @Input() vScrollHeightPx = 250;
  @Input() initialMaxItems: number = 0;
  @Input() toggleHiddenListLabel: string = 'weitere';
  @Input() fontSize = 'var(--ep-font-size-body-xs)';
  @Input() templateRef!: TemplateRef<any>;
  @Output() private selectEvent: EventEmitter<any> = new EventEmitter();
  @Output() private expandEvent: EventEmitter<any> = new EventEmitter();
  @Output() private completeEvent: EventEmitter<any> = new EventEmitter();
  @Output() private searchResetEvent: EventEmitter<any> = new EventEmitter();
  @ViewChild(CdkVirtualScrollViewport, ) virtualScroll: CdkVirtualScrollViewport | undefined;
  searchText = '';
  searchControl = new FormControl();
  displaySource: FlatNode[] = [];
  isItemHidden: boolean = false;
  sortOrder = 0;
  radioGroup = Guid.create().toString();
  private flatNodeMap = new Map<FlatNode, Node>();
  private nestedNodeMap = new Map<Node, FlatNode>();
  private treeControl: FlatTreeControl<FlatNode>;
  private treeFlattener: MatTreeFlattener<Node, FlatNode>;
  private dataSource: MatTreeFlatDataSource<Node, FlatNode>;
  private checklistSelection: SelectionModel<FlatNode>;

  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<FlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.checklistSelection = new SelectionModel<FlatNode>(this.multiple);
  }

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        filter((a) => trim(a).length === 0)
      )
      .subscribe(() => {
        this.showAll(this.nestedList);
        this.dataSource.data = this.data;
        this.displaySource = this.flatList.filter((a) => a.level === 0);
        this.treeControl.collapseAll();
        this.scrollToIndex(0);
        this.searchResetEvent.emit(true);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.dataSource.data = this.data;

      this.displaySource = this.flatList.filter((a) => a.level === 0);
      if (this.checklistSelection) {
        this.checklistSelection.clear();
      }
      this.treeControl.collapseAll();
      this.resetNodes();
      if (this.data.length > 0) {
        this.completeEvent.emit(true);
      }
    }

    if (changes['multiple']) {
      this.checklistSelection = new SelectionModel<FlatNode>(this.multiple);
    }
  }

  private getLevel = (node: FlatNode) => node.level;

  private isExpandable = (node: FlatNode) => node.expandable;

  private getChildren = (node: Node): Node[] | undefined => node.children;

  isExpanded = (node: FlatNode) => this.treeControl.isExpanded(node);

  private get nestedList(): Node[] {
    return this.dataSource.data;
  }

  public get flatList(): FlatNode[] {
    return this.treeControl.dataNodes;
  }

  private transformer = (node: Node, level: number) => {
    if (isNil(node.children)) {
      node.children = [];
    }

    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode?.item === node.item ? existingNode : ({} as FlatNode);

    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = node.children.length > 0;
    flatNode.item.sortOrder = this.sortOrder++;

    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  private descendantsAllSelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return (
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      })
    );
  }

  descendantsPartiallySelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  private nodeSelectionToggle(node: FlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  private leafNodeSelectionToggle(node: FlatNode): void {
    this.checklistSelection.toggle(node);
    if (this.multiple) {
      this.checkAllParentsSelection(node);
    }
  }

  private checkAllParentsSelection(node: FlatNode): void {
    let parent: FlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  private checkRootNodeSelection(node: FlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  private getParentNode(node: FlatNode): FlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.flatList.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.flatList[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  isChecked(node: FlatNode): boolean {
    if (node.expandable) {
      return this.descendantsAllSelected(node);
    } else {
      return this.checklistSelection.isSelected(node);
    }
  }

  checkChanged(node: FlatNode, emit: boolean): void {
    if (node.expandable) {
      this.nodeSelectionToggle(node);
    } else {
      this.leafNodeSelectionToggle(node);
    }

    if (emit) {
      this.selectEvent.emit({
        isChecked: this.checklistSelection.isSelected(node),
        node,
      });
    }
  }

  toggleExpand(node: FlatNode, index: number): void {
    this.treeControl.toggle(node);
    const isExpanded = this.isExpanded(node);

    const nestedNode = this.flatNodeMap.get(node);
    if (isExpanded) {
      const children = nestedNode?.children || [];
      const childrenFlatNodes = intersectionWith(this.flatList, children, (a, b) => a.item.id === b.item.id);
      const parentIndex = findIndex(this.displaySource, (a) => a.item.id === node.item.id) + 1;
      this.displaySource.splice(
        parentIndex,
        0,
        ...childrenFlatNodes.filter((childNode) => {
          if (this.searchText.trim().toLowerCase().length <= 0) {
            return true;
          }
          return childNode.item.filterMatch;
        })
      );
      this.displaySource = [...this.displaySource];
    } else {
      const allChildren: any = [];
      if (nestedNode) {
        this.collapseChildren(nestedNode.children, allChildren);
      }
      this.displaySource = differenceWith(this.displaySource, allChildren, (a: FlatNode, b: any) => a.item.id === b.item.id);
    }

    this.expandEvent.emit({
      isExpanded,
      node,
      index,
    });
  }

  private collapseChildren(children: Node[] | undefined, collapseList: Node[]): void {
    children?.forEach((node: Node) => {
      collapseList.push(node);
      const flatNode = this.nestedNodeMap.get(node);
      if (flatNode) {
        this.treeControl.collapse(flatNode);
      }

      if (node.children) {
        this.collapseChildren(node.children, collapseList);
      }
    });
  }

  search() {
    const filterText = this.searchText.trim().toLowerCase();

    if (filterText.length <= 0) {
      return;
    }

    this.displaySource = [];
    this.filter(filterText, this.nestedList);
    this.displaySource = sortBy(this.displaySource, (a) => a.item.sortOrder);
    this.scrollToIndex(0);
  }

  private filter(filterText: string, list: Node[] | undefined): FlatNode[] {
    const result: FlatNode[] = [];

    list?.forEach((node) => {
      const flatNode = this.nestedNodeMap.get(node) as FlatNode;
      node.item.filterMatch = node.item.name.trim().toLowerCase().indexOf(filterText) === 0;

      if (node.item.filterMatch) {
        this.addToDisplayOnSearch(flatNode);
        this.showAll(node.children);
      }

      if (!node.item.filterMatch) {
        const matchedChildren = this.filter(filterText, node.children);
        node.item.filterMatch = matchedChildren.length > 0;
        if (node.item.filterMatch) {
          this.addToDisplay(flatNode);
          this.treeControl.expand(flatNode);
          matchedChildren.forEach((childNode) => {
            this.addToDisplay(childNode);
          });
        }
      }

      if (node.item.filterMatch) {
        result.push(flatNode);
      }
    });

    return result;
  }

  private addToDisplay(node: FlatNode | undefined): void {
    if (!isNil(node) && !this.displaySource.find((a) => a.item.id === node.item.id)) {
      this.displaySource.push(node);
    }
  }

  private addToDisplayOnSearch(node: FlatNode | undefined): void {
    if (!isNil(node)) {
      this.displaySource.push(node);
    }
  }

  private showAll(list: Node[] | undefined): void {
    list?.forEach((node) => {
      node.item.filterMatch = true;
      if (node.children) {
        this.showAll(node.children);
      }
    });
  }

  resetNodes() {
    this.flatList.forEach((a) => {
      a.item.disabled = false;
      a.item.filterMatch = true;
    });
  }

  trackByFn(_: number, node: FlatNode): string {
    return node.item.id;
  }

  public toggleNodeExpansionByLevel(level: number): void {
    this.flatList.forEach((a) => {
      if (a.level === level) {
        this.toggleExpand(a, 0);
      }
    });
  }

  public get selectedNodes(): FlatNode[] {
    return this.checklistSelection.selected;
  }

  public clearSelections(): void {
    this.checklistSelection.clear();
  }

  public scrollToIndex(index: number): void {
    if (this.virtualScroll) {
      this.virtualScroll.scrollToIndex(index);
    }
  }

  public disableNodeByLevel(level: number, isDisabled: boolean): void {
    this.flatList.forEach((a) => {
      if (a.level === level) {
        a.item.disabled = isDisabled;
      }
    });
  }

  public selectNodes(ids: string[]): void {
    const selFlatNodes = intersectionWith(this.flatList, ids, (a, b) => a.item.id === b);
    selFlatNodes.forEach((flatNode) => {
      this.checkChanged(flatNode, false);
    });
  }

  public toggleHiddenList() {
    this.isItemHidden = !this.isItemHidden;
  }
}
